import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { adminAnchorService } from "../../services/adminAnchorService";
import { CREDIT_API_URL } from "../../constants";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import showToast from "../../utils/toast";
import AnchorDetailsModal from "../../components/admin/AnchorDetailsModal";

const ViewAnchorsPage = ({ addNotification }) => {
    const { user } = useSelector(state => state.auth);
    const navigate = useNavigate();
    
    // State for anchors data
    const [anchors, setAnchors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState(null);
    
    // Filter states
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [gstNumber, setGstNumber] = useState("");
    const [verificationStatus, setVerificationStatus] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactMobile, setContactMobile] = useState("");
    
    // Pagination
    const [page, setPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [hasMoreAnchors, setHasMoreAnchors] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Modal states
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedAnchor, setSelectedAnchor] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [anchorToReject, setAnchorToReject] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [customRejectionReason, setCustomRejectionReason] = useState("");
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [anchorToApprove, setAnchorToApprove] = useState(null);
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [currentAnchorForRemarks, setCurrentAnchorForRemarks] = useState(null);
    const [remarksToDisplay, setRemarksToDisplay] = useState('');
    const [newRemarkText, setNewRemarkText] = useState('');
    
    const scrollPositionRef = useRef(0);

    useEffect(() => {
        // Check for auth token
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        fetchAnchors();
    }, [navigate, page, recordsPerPage]);

    useEffect(() => {
        if (!loading && anchors.length > 0 && scrollPositionRef.current !== 0) {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0;
        }
    }, [loading, anchors]);

    // Fetch anchors data
    const fetchAnchors = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        
        scrollPositionRef.current = window.scrollY;
        setLoading(true);
        setError(null);
        
        try {
            const filters = {
                startDate: startDate ? formatDate(startDate) : "",
                endDate: endDate ? formatDate(endDate) : "",
                companyName: companyName.trim(),
                gstNumber: gstNumber.trim(),
                status: verificationStatus,
                contactName: contactName.trim(),
                contactMobile: contactMobile.trim(),
                page,
                limit: recordsPerPage
            };

            const result = await adminAnchorService.getAnchors(filters);
            
            if (result.status === 'Success' && result.data) {
                const anchorsData = result.data.anchors || result.data;
                const pagination = result.data.pagination || {};
                
                if (Array.isArray(anchorsData) && anchorsData.length > 0) {
                    setAnchors(anchorsData);
                    setTotalRecords(pagination.total || anchorsData.length);
                    setHasMoreAnchors(pagination.page < pagination.totalPages);
                    setNoDataMessage(null);
                } else {
                    setAnchors([]);
                    setNoDataMessage("No anchor companies found within the specified criteria.");
                    setHasMoreAnchors(false);
                }
            } else {
                setAnchors([]);
                setNoDataMessage("No anchor companies found.");
                setHasMoreAnchors(false);
            }
        } catch (err) {
            console.error("Error fetching anchors:", err);
            setError(err.message);
            setAnchors([]);
            setHasMoreAnchors(false);
            showToast(err.message || 'Failed to fetch anchors', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format date as dd-mm-yyyy
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Helper function to format date for display
    const formatDateForDisplay = (isoDateString) => {
        if (!isoDateString) return 'N/A';
        const date = new Date(isoDateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Handle filter submit
    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        fetchAnchors();
    };

    // Handle clear filter
    const handleClearFilter = () => {
        setStartDate("");
        setEndDate("");
        setCompanyName("");
        setGstNumber("");
        setVerificationStatus("");
        setContactName("");
        setContactMobile("");
        setPage(1);
        
        // Fetch anchors with cleared filters
        setTimeout(() => {
            fetchAnchors();
        }, 100);
    };

    // Handle view details
    const handleViewDetails = async (anchor) => {
        try {
            setLoading(true);
            const result = await adminAnchorService.getAnchorDetails(anchor.anchorId || anchor.id);
            if (result.status === 'Success' && result.data) {
                setSelectedAnchor(result.data);
                setShowDetailsModal(true);
            }
        } catch (err) {
            console.error("Error fetching anchor details:", err);
            showToast(err.message || 'Failed to fetch anchor details', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle approve anchor - open modal
    const handleApproveClick = (anchor) => {
        setAnchorToApprove(anchor);
        setShowApproveModal(true);
    };

    // Handle confirm approval
    const handleConfirmApproval = async () => {
        if (!anchorToApprove) return;

        try {
            setLoading(true);
            // Add timestamp to approval remarks (IST format)
            const timestamp = new Date().toLocaleString('en-IN', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: false,
                timeZone: 'Asia/Kolkata'
            });
            const approvalRemarksWithTimestamp = `[${timestamp}] Documents verified successfully`;
            
            const result = await adminAnchorService.approveAnchor(
                anchorToApprove.anchorId || anchorToApprove.id,
                approvalRemarksWithTimestamp
            );
            
            if (result.status === 'Success') {
                showToast('Anchor approved successfully', 'success');
                addNotification('Anchor approved successfully', 'success');
                
                // Update local state with response data
                const updatedAnchor = result.data || {};
                setAnchors(prev => prev.map(a => 
                    (a.anchorId || a.id) === (anchorToApprove.anchorId || anchorToApprove.id) 
                        ? { 
                            ...a, 
                            verificationStatus: updatedAnchor.verificationStatus || 'Verified',
                            remarks: updatedAnchor.remarks || a.remarks,
                            approvalRemarks: updatedAnchor.approvalRemarks || updatedAnchor.remarks || a.approvalRemarks
                          } 
                        : a
                ));
                
                // Trigger external notifications: first AnchorVerified, then SupplierNotification (non-blocking)
                try {
                    const _notifyBase = CREDIT_API_URL;
                    if (!_notifyBase) {
                        console.warn('CREDIT_API_URL is not set; skipping notifications for anchor', anchorToApprove?.anchorId || anchorToApprove?.id);
                    } else {
                        const _notifyUrl = `${_notifyBase.replace(/\/+$/,'')}/anchor/trigger-notification`;

                        const anchorVerifiedPayload = {
                            eventType: "AnchorVerified",
                            mobile: anchorToApprove?.contactMobile || anchorToApprove?.mobile || "",
                            data: {
                                companyName: anchorToApprove?.companyName || "",
                                anchorId: anchorToApprove?.anchorId || anchorToApprove?.id || ""
                            }
                        };
                        if (anchorToApprove?.leadId) {
                            anchorVerifiedPayload.leadId = anchorToApprove.leadId;
                        }

                        // Send AnchorVerified, then on completion send SupplierNotification
                        fetch(_notifyUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(anchorVerifiedPayload)
                        }).then(async (res) => {
                            if (!res.ok) {
                                const errText = await res.text();
                                console.warn('Notification API (AnchorVerified) returned non-OK:', res.status, errText);
                            }

                            // Prepare SupplierNotification payload
                            const supplierPayload = {
                                eventType: "SupplierNotification",
                                mobile: anchorToApprove?.contactMobile || anchorToApprove?.mobile || "",
                                data: {
                                    companyName: anchorToApprove?.companyName || "",
                                    anchorId: anchorToApprove?.anchorId || anchorToApprove?.id || ""
                                }
                            };
                            if (anchorToApprove?.leadId) {
                                supplierPayload.leadId = anchorToApprove.leadId;
                            }

                            // Fire supplier notification (do not block UI)
                            fetch(_notifyUrl, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(supplierPayload)
                            }).then(async (res2) => {
                                if (!res2.ok) {
                                    const errText2 = await res2.text();
                                    console.warn('Notification API (SupplierNotification) returned non-OK:', res2.status, errText2);
                                }
                            }).catch((err2) => {
                                console.warn('Failed to send SupplierNotification:', err2);
                            });
                        }).catch((err) => {
                            console.warn('Failed to send AnchorVerified notification:', err);
                            // attempt SupplierNotification even if AnchorVerified failed
                            const supplierPayloadFallback = {
                                eventType: "SupplierNotification",
                                mobile: anchorToApprove?.contactMobile || anchorToApprove?.mobile || "",
                                data: {
                                    companyName: anchorToApprove?.companyName || "",
                                    anchorId: anchorToApprove?.anchorId || anchorToApprove?.id || ""
                                }
                            };
                            if (anchorToApprove?.leadId) {
                                supplierPayloadFallback.leadId = anchorToApprove.leadId;
                            }
                            fetch(_notifyUrl, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(supplierPayloadFallback)
                            }).catch((err2) => {
                                console.warn('Failed to send SupplierNotification after AnchorVerified error:', err2);
                            });
                        });
                    }
                } catch (notifErr) {
                    console.warn('Error preparing approval notifications:', notifErr);
                }
                
                setShowApproveModal(false);
                setAnchorToApprove(null);
            }
        } catch (err) {
            console.error("Error approving anchor:", err);
            showToast(err.message || 'Failed to approve anchor', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle reject anchor - open modal
    const handleRejectClick = (anchor) => {
        setAnchorToReject(anchor);
        setRejectionReason("");
        setCustomRejectionReason("");
        setShowRejectModal(true);
    };

    // Handle confirm rejection
    const handleConfirmRejection = async () => {
        // Determine final rejection reason
        const finalReason = rejectionReason === 'Other' ? customRejectionReason.trim() : rejectionReason;
        
        if (!finalReason) {
            showToast('Please select or provide a rejection reason', 'error');
            return;
        }

        try {
            setLoading(true);
            // Add timestamp to rejection reason (IST format)
            const timestamp = new Date().toLocaleString('en-IN', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: false,
                timeZone: 'Asia/Kolkata'
            });
            const finalReasonWithTimestamp = `[${timestamp}] ${finalReason}`;
            
            const result = await adminAnchorService.rejectAnchor(
                anchorToReject.anchorId || anchorToReject.id,
                finalReasonWithTimestamp
            );
            
            if (result.status === 'Success') {
                showToast('Anchor rejected successfully', 'success');
                addNotification('Anchor rejected successfully', 'info');
                
                // Update local state with response data
                const updatedAnchor = result.data || {};
                setAnchors(prev => prev.map(a => 
                    (a.anchorId || a.id) === (anchorToReject.anchorId || anchorToReject.id) 
                        ? { 
                            ...a, 
                            verificationStatus: updatedAnchor.verificationStatus || 'Rejected',
                            remarks: updatedAnchor.remarks || a.remarks,
                            rejectionReason: updatedAnchor.rejectionReason || finalReasonWithTimestamp || a.rejectionReason
                          } 
                        : a
                ));
                
                // Trigger external notification for anchor rejected (non-blocking)
                try {
                    const notifyPayload = {
                        eventType: "AnchorRejected",
                        mobile: anchorToReject?.contactMobile || anchorToReject?.mobile || "",
                        data: {
                            companyName: anchorToReject?.companyName || "",
                            anchorId: anchorToReject?.anchorId || anchorToReject?.id || "",
                            reason: finalReasonWithTimestamp || finalReason
                        }
                    };
                    if (anchorToReject?.leadId) {
                        notifyPayload.leadId = anchorToReject.leadId;
                    }

                    // fire-and-forget notification call using environment-configured CREDIT_API_URL
                    const _notifyBase = CREDIT_API_URL;
                    if (!_notifyBase) {
                        console.warn('CREDIT_API_URL is not set; skipping rejection notification for anchor', anchorToReject?.anchorId || anchorToReject?.id);
                    } else {
                        const _notifyUrl = `${_notifyBase.replace(/\/+$/,'')}/anchor/trigger-notification`;
                        fetch(_notifyUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(notifyPayload)
                        }).then(async (res) => {
                            if (!res.ok) {
                                const errText = await res.text();
                                console.warn('Notification API (reject) returned non-OK:', res.status, errText);
                            }
                        }).catch((err) => {
                            console.warn('Failed to send rejection notification:', err);
                        });
                    }
                } catch (notifErr) {
                    console.warn('Error preparing rejection notification:', notifErr);
                }
                
                setShowRejectModal(false);
                setAnchorToReject(null);
                setRejectionReason("");
                setCustomRejectionReason("");
            }
        } catch (err) {
            console.error("Error rejecting anchor:", err);
            showToast(err.message || 'Failed to reject anchor', 'error');
        } finally {
            setLoading(false);
        }
    };
    
    // Handle view/add notes
    const handleViewNotes = (anchor) => {
        const allRemarks = [];
        if (anchor.approvalRemarks) {
            allRemarks.push(`[Approval] ${anchor.approvalRemarks}`);
        }
        if (anchor.rejectionReason) {
            allRemarks.push(`[Rejection] ${anchor.rejectionReason}`);
        }
        if (anchor.remarks) {
            allRemarks.push(anchor.remarks);
        }
        const combinedRemarks = allRemarks.join('\n');
        setRemarksToDisplay(combinedRemarks);
        setCurrentAnchorForRemarks(anchor);
        setNewRemarkText('');
        setShowRemarksModal(true);
    };

    // Handle save remarks
    const handleRemarksModalSave = async () => {
        if (!newRemarkText.trim()) {
            showToast('Note cannot be empty', 'error');
            return;
        }

        if (!currentAnchorForRemarks) return;

        const anchorId = currentAnchorForRemarks.anchorId || currentAnchorForRemarks.id;
        const timestamp = new Date().toLocaleString();
        const newNoteEntry = `[${timestamp}] ${newRemarkText.trim()}`;
        
        // Combine with existing remarks
        const updatedRemarks = remarksToDisplay 
            ? `${newNoteEntry}\n${remarksToDisplay}` 
            : newNoteEntry;

        try {
            // Call API to save remarks
            await adminAnchorService.updateAnchorRemarks(anchorId, {
                remarks: updatedRemarks
            });

            // Update local state after successful API call
            setAnchors(prev => prev.map(a => 
                (a.anchorId || a.id) === anchorId 
                    ? { ...a, remarks: updatedRemarks }
                    : a
            ));

            // Update displayed remarks
            setRemarksToDisplay(updatedRemarks);
            setNewRemarkText('');
            setCurrentAnchorForRemarks(prev => ({ ...prev, remarks: updatedRemarks }));
            
            showToast('Note added successfully', 'success');
            if (addNotification) {
                addNotification(`Note added to anchor ${currentAnchorForRemarks.companyName || anchorId}`, 'info');
            }
        } catch (error) {
            console.error('Error saving note:', error);
            showToast(error.message || 'Failed to save note', 'error');
        }
    };

    // Check if rejection can be confirmed
    const canConfirmRejection = () => {
        if (rejectionReason === 'Other') {
            return customRejectionReason.trim().length > 0;
        }
        return rejectionReason.length > 0;
    };



    // Render status badge - simple text based
    const renderStatusBadge = (status) => {
        return status || 'Pending';
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showToast('Copied to clipboard', 'success');
    };


    if (loading && anchors.length === 0) {
        return (
            <>
                <Header />
                <div id="wrapper" style={{ paddingTop: '70px' }}>
                    <div className="content">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body text-center" style={{ padding: '50px' }}>
                                        <i className="fa fa-spinner fa-spin fa-3x text-muted"></i>
                                        <h4 className="m-t-md text-muted">Loading anchors...</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    {/* Header Section */}
                                    <div style={{ marginBottom: '25px' }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            marginBottom: '20px'
                                        }}>
                                            <div>
                                                <h2 className="font-light" style={{ margin: '0 0 5px 0' }}>
                                                    View Anchor Companies
                                                </h2>
                                                <p className="text-muted" style={{ margin: 0, fontSize: '14px' }}>
                                                    Manage and verify anchor company registrations
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Filters Section */}
                                    <form onSubmit={handleFilterSubmit}>
                                        <div className="row">
                                            {/* Row 1 */}
                                            <div className="form-group col-sm-3">
                                                <label htmlFor="startDate" style={{ marginBottom: '8px', display: 'block' }}>Start Date</label>
                                                <input
                                                    type="date"
                                                    id="startDate"
                                                    className="form-control"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group col-sm-3">
                                                <label htmlFor="endDate" style={{ marginBottom: '8px', display: 'block' }}>End Date</label>
                                                <input
                                                    type="date"
                                                    id="endDate"
                                                    className="form-control"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group col-sm-3">
                                                <label htmlFor="companyName" style={{ marginBottom: '8px', display: 'block' }}>Company Name</label>
                                                <input
                                                    type="text"
                                                    id="companyName"
                                                    className="form-control"
                                                    placeholder="Enter company name"
                                                    value={companyName}
                                                    onChange={(e) => setCompanyName(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group col-sm-3">
                                                <label htmlFor="gstNumber" style={{ marginBottom: '8px', display: 'block' }}>GST Number</label>
                                                <input
                                                    type="text"
                                                    id="gstNumber"
                                                    className="form-control"
                                                    placeholder="Enter GST number"
                                                    value={gstNumber}
                                                    onChange={(e) => setGstNumber(e.target.value)}
                                                />
                                            </div>

                                            {/* Row 2 */}
                                            <div className="form-group col-sm-3">
                                                <label htmlFor="verificationStatus" style={{ marginBottom: '8px', display: 'block' }}>Verification Status</label>
                                                <select
                                                    id="verificationStatus"
                                                    className="form-control"
                                                    value={verificationStatus}
                                                    onChange={(e) => setVerificationStatus(e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Verified">Verified</option>
                                                    <option value="Rejected">Rejected</option>
                                                </select>
                                            </div>

                                            <div className="form-group col-sm-3">
                                                <label htmlFor="contactName" style={{ marginBottom: '8px', display: 'block' }}>Contact Name</label>
                                                <input
                                                    type="text"
                                                    id="contactName"
                                                    className="form-control"
                                                    placeholder="Enter contact name"
                                                    value={contactName}
                                                    onChange={(e) => setContactName(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group col-sm-3">
                                                <label htmlFor="contactMobile" style={{ marginBottom: '8px', display: 'block' }}>Contact Mobile</label>
                                                <input
                                                    type="text"
                                                    id="contactMobile"
                                                    className="form-control"
                                                    placeholder="Enter mobile number"
                                                    value={contactMobile}
                                                    onChange={(e) => setContactMobile(e.target.value)}
                                                />
                                            </div>

                                            {/* Action Buttons Row */}
                                            <div className="col-sm-12" style={{ marginTop: '10px' }}>
                                                    <div style={{ display: 'flex', gap: '10px' }}>
                                                        <button 
                                                            type="submit" 
                                                            className="btn btn-primary"
                                                        >
                                                            <i className="fa fa-filter"></i> Filter
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-default"
                                                            onClick={handleClearFilter}
                                                        >
                                                            <i className="fa fa-refresh"></i> Clear Filter
                                                        </button>
                                                    </div>
                                            </div>
                                        </div>
                                    </form>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error/No Data Messages */}
                    {error && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="alert alert-danger" role="alert">
                                    <i className="fa fa-exclamation-triangle"></i> {error}
                                </div>
                            </div>
                        </div>
                    )}
                    {noDataMessage && !error && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="alert alert-info" role="alert">
                                    <i className="fa fa-info-circle"></i> {noDataMessage}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Table Section - Separate Panel */}
                    {anchors.length > 0 && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body">
                                        <div className="table-responsive">
                                            <table className="table table-striped table-bordered table-hover">
                                                <thead>
                                                    <tr>
                                                        <th style={{ minWidth: '160px', textAlign: 'center' }}>Created At</th>
                                                        <th style={{ minWidth: '180px', textAlign: 'center' }}>Anchor ID</th>
                                                        <th style={{ minWidth: '200px', textAlign: 'center' }}>Company Name</th>
                                                        <th style={{ minWidth: '150px', textAlign: 'center' }}>GST Number</th>
                                                        <th style={{ minWidth: '150px', textAlign: 'center' }}>Contact Name</th>
                                                        <th style={{ minWidth: '140px', textAlign: 'center' }}>Contact Mobile</th>
                                                        <th style={{ minWidth: '150px', textAlign: 'center' }}>Registration Source</th>
                                                        <th style={{ minWidth: '140px', textAlign: 'center' }}>Verification Status</th>
                                                        <th style={{ minWidth: '180px', textAlign: 'center' }}>Actions</th>
                                                        <th style={{ minWidth: '150px', textAlign: 'center' }}>Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {anchors.map((anchor, index) => (
                                                        <tr key={anchor.anchorId || anchor.id || index}>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {formatDateForDisplay(anchor.createdAt)}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                    <span
                                                                        onClick={() => handleViewDetails(anchor)}
                                                                        style={{ 
                                                                            color: '#333',
                                                                            fontWeight: '500',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                                    >
                                                                        {anchor.anchorId || anchor.id || 'N/A'}
                                                                    </span>
                                                                    <button
                                                                        className="btn btn-xs btn-default"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            copyToClipboard(anchor.anchorId || anchor.id);
                                                                        }}
                                                                        title="Copy Anchor ID"
                                                                        style={{ padding: '2px 6px', fontSize: '10px' }}
                                                                    >
                                                                        <i className="fa fa-copy"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.companyName || 'N/A'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.gstNumber || 'N/A'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.contactName || 'N/A'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.contactMobile || 'N/A'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.registrationSource || 'N/A'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {renderStatusBadge(anchor.verificationStatus)}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                {anchor.verificationStatus === 'Pending' ? (
                                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                                        <button 
                                                                            className="btn btn-success btn-sm" 
                                                                            onClick={() => handleApproveClick(anchor)}
                                                                        >
                                                                            Accept
                                                                        </button>
                                                                        <button 
                                                                            className="btn btn-danger btn-sm" 
                                                                            onClick={() => handleRejectClick(anchor)}
                                                                        >
                                                                            Reject
                                                                        </button>
                                                                    </div>
                                                                ) : anchor.verificationStatus === 'Verified' ? (
                                                                    <span style={{ color: '#4CAF50', fontWeight: 600 }}>
                                                                        ✅ Accepted
                                                                    </span>
                                                                ) : anchor.verificationStatus === 'Rejected' ? (
                                                                    <span style={{ color: '#F44336', fontWeight: 600 }}>
                                                                        ❌ Rejected
                                                                    </span>
                                                                ) : null}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <span 
                                                                    className="text-primary" 
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        textDecoration: 'underline',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '5px'
                                                                    }}
                                                                    onClick={() => {
                                                                        const allRemarks = [];
                                                                        if (anchor.approvalRemarks) {
                                                                            allRemarks.push(`[Approval] ${anchor.approvalRemarks}`);
                                                                        }
                                                                        if (anchor.rejectionReason) {
                                                                            allRemarks.push(`[Rejection] ${anchor.rejectionReason}`);
                                                                        }
                                                                        if (anchor.remarks) {
                                                                            allRemarks.push(anchor.remarks);
                                                                        }
                                                                        const combinedRemarks = allRemarks.join('\n');
                                                                        setRemarksToDisplay(combinedRemarks);
                                                                        setCurrentAnchorForRemarks(anchor);
                                                                        setNewRemarkText('');
                                                                        setShowRemarksModal(true);
                                                                    }}
                                                                >
                                                                    <i className={anchor.remarks || anchor.approvalRemarks || anchor.rejectionReason ? "fa fa-eye" : "fa fa-plus"}></i>
                                                                    {anchor.remarks || anchor.approvalRemarks || anchor.rejectionReason ? 'View Notes' : 'Add Note'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="row" style={{ marginTop: '20px' }}>
                                            <div className="col-sm-6">
                                                <div style={{ padding: '10px 0' }}>
                                                    Showing {((page - 1) * recordsPerPage) + 1} to {Math.min(page * recordsPerPage, totalRecords)} of {totalRecords} entries
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                                    <button
                                                        className="btn btn-default"
                                                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                                        disabled={page === 1}
                                                    >
                                                        <i className="fa fa-chevron-left"></i> Previous
                                                    </button>
                                                    <button
                                                        className="btn btn-default"
                                                        onClick={() => setPage(prev => prev + 1)}
                                                        disabled={!hasMoreAnchors}
                                                    >
                                                        Next <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* Anchor Details Modal */}
            {showDetailsModal && selectedAnchor && (
                <AnchorDetailsModal
                    anchor={selectedAnchor}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedAnchor(null);
                    }}
                />
            )}

            {/* Approve Confirmation Modal */}
            {showApproveModal && (
                <div className="modal fade in" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" style={{ marginTop: '100px' }}>
                        <div className="modal-content" style={{ borderRadius: '4px', border: 'none', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
                            {/* Header */}
                            <div className="modal-header" style={{ 
                                borderBottom: 'none', 
                                padding: '30px 30px 20px 30px'
                            }}>
                                <button 
                                    type="button" 
                                    className="close" 
                                    onClick={() => {
                                        setShowApproveModal(false);
                                        setAnchorToApprove(null);
                                    }}
                                    style={{ fontSize: '28px', fontWeight: '300', color: '#999', opacity: '1' }}
                                >
                                    &times;
                                </button>
                                <h4 className="modal-title" style={{ 
                                    margin: 0, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    fontSize: '24px',
                                    fontWeight: '400',
                                    color: '#333'
                                }}>
                                    <i className="fa fa-check-circle" style={{ color: '#1ab394', fontSize: '28px' }}></i>
                                    Approve Anchor Company
                                </h4>
                            </div>
                            
                            {/* Body */}
                            <div className="modal-body" style={{ padding: '23px 30px 20px 30px' }}>
                                {/* Anchor Details */}
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <span style={{ fontWeight: '600', color: '#333' }}>Anchor ID: </span>
                                        <span style={{ color: '#555' }}>{anchorToApprove?.anchorId}</span>
                                    </div>
                                    <div style={{ marginBottom: '8px' }}>
                                        <span style={{ fontWeight: '600', color: '#333' }}>Company: </span>
                                        <span style={{ color: '#555' }}>{anchorToApprove?.companyName}</span>
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: '600', color: '#333' }}>GST Number: </span>
                                        <span style={{ color: '#555' }}>{anchorToApprove?.gstNumber}</span>
                                    </div>
                                </div>

                                {/* Info Message */}
                                <div style={{ 
                                    backgroundColor: '#dff0d8', 
                                    border: '1px solid #d6e9c6',
                                    borderRadius: '4px',
                                    padding: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <i className="fa fa-info-circle" style={{ color: '#3c763d', fontSize: '16px' }}></i>
                                    <span style={{ color: '#3c763d', fontSize: '14px' }}>
                                        The anchor company will be verified and can start using the platform.
                                    </span>
                                </div>
                            </div>
                            
                            {/* Footer */}
                            <div className="modal-footer" style={{ 
                                borderTop: 'none', 
                                padding: '20px 30px 30px 30px',
                                textAlign: 'right'
                            }}>
                                <button 
                                    type="button" 
                                    className="btn btn-default" 
                                    onClick={() => {
                                        setShowApproveModal(false);
                                        setAnchorToApprove(null);
                                    }}
                                    style={{ marginRight: '10px', padding: '8px 20px' }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-success"
                                    onClick={handleConfirmApproval}
                                    style={{ padding: '8px 20px' }}
                                >
                                    <i className="fa fa-check"></i> Confirm Approval
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="modal fade in" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" style={{ marginTop: '100px' }}>
                        <div className="modal-content" style={{ borderRadius: '4px', border: 'none', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
                            {/* Header */}
                            <div className="modal-header" style={{ 
                                borderBottom: 'none', 
                                padding: '30px 30px 20px 30px'
                            }}>
                                <button 
                                    type="button" 
                                    className="close" 
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setAnchorToReject(null);
                                        setRejectionReason("");
                                        setCustomRejectionReason("");
                                    }}
                                    style={{ fontSize: '28px', fontWeight: '300', color: '#999', opacity: '1' }}
                                >
                                    &times;
                                </button>
                                <h4 className="modal-title" style={{ 
                                    margin: 0, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    fontSize: '24px',
                                    fontWeight: '400',
                                    color: '#333'
                                }}>
                                    <i className="fa fa-times-circle" style={{ color: '#ed5565', fontSize: '28px' }}></i>
                                    Reject Anchor Company
                                </h4>
                            </div>
                            
                            {/* Body */}
                            <div className="modal-body" style={{ padding: '23px 30px 20px 30px' }}>
                                {/* Anchor Details */}
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <span style={{ fontWeight: '600', color: '#333' }}>Anchor ID: </span>
                                        <span style={{ color: '#555' }}>{anchorToReject?.anchorId}</span>
                                    </div>
                                    <div style={{ marginBottom: '8px' }}>
                                        <span style={{ fontWeight: '600', color: '#333' }}>Company: </span>
                                        <span style={{ color: '#555' }}>{anchorToReject?.companyName}</span>
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: '600', color: '#333' }}>GST Number: </span>
                                        <span style={{ color: '#555' }}>{anchorToReject?.gstNumber}</span>
                                    </div>
                                </div>

                                {/* Reason Dropdown */}
                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                    <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                        Reason for Rejection <span style={{ color: '#ed5565' }}>*</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        value={rejectionReason}
                                        onChange={(e) => {
                                            setRejectionReason(e.target.value);
                                            if (e.target.value !== 'Other') {
                                                setCustomRejectionReason("");
                                            }
                                        }}
                                        style={{ height: '42px', fontSize: '14px' }}
                                    >
                                        <option value="">-- Select Reason --</option>
                                        <option value="Invalid GST documents">Invalid GST documents</option>
                                        <option value="Incomplete documentation">Incomplete documentation</option>
                                        <option value="Company verification failed">Company verification failed</option>
                                        <option value="Suspicious activity detected">Suspicious activity detected</option>
                                        <option value="Duplicate registration">Duplicate registration</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                {/* Custom Reason Textarea - only show when "Other" is selected */}
                                {rejectionReason === 'Other' && (
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label style={{ fontWeight: '600', color: '#333', marginBottom: '8px', display: 'block' }}>
                                            Please specify reason <span style={{ color: '#ed5565' }}>*</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            placeholder="Enter custom rejection reason..."
                                            value={customRejectionReason}
                                            onChange={(e) => setCustomRejectionReason(e.target.value)}
                                            style={{ resize: 'vertical', fontSize: '14px' }}
                                        />
                                    </div>
                                )}

                                {/* Warning Message */}
                                <div style={{ 
                                    backgroundColor: '#fcf8e3', 
                                    border: '1px solid #faebcc',
                                    borderRadius: '4px',
                                    padding: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <i className="fa fa-exclamation-triangle" style={{ color: '#8a6d3b', fontSize: '16px' }}></i>
                                    <span style={{ color: '#8a6d3b', fontSize: '14px' }}>
                                        This action cannot be undone. The anchor will be marked as rejected.
                                    </span>
                                </div>
                            </div>
                            
                            {/* Footer */}
                            <div className="modal-footer" style={{ 
                                borderTop: 'none', 
                                padding: '20px 30px 30px 30px',
                                textAlign: 'right'
                            }}>
                                <button 
                                    type="button" 
                                    className="btn btn-default" 
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setAnchorToReject(null);
                                        setRejectionReason("");
                                        setCustomRejectionReason("");
                                    }}
                                    style={{ marginRight: '10px', padding: '8px 20px' }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleConfirmRejection}
                                    disabled={!canConfirmRejection()}
                                    style={{ padding: '8px 20px' }}
                                >
                                    <i className="fa fa-times"></i> Confirm Rejection
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remarks Modal - View & Add Notes */}
            {showRemarksModal && currentAnchorForRemarks && (
                <div className="modal" style={{ 
                    display: 'flex', 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '700px', width: '90%', margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                <h5 className="modal-title" style={{ 
                                    color: '#495057', 
                                    fontWeight: '600',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <i className="fa fa-comments-o" style={{ color: '#007bff', fontSize: '20px' }}></i>
                                    <span>Notes & Remarks</span>
                                    <span style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500',
                                        color: '#6c757d',
                                        backgroundColor: '#e9ecef',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontFamily: 'monospace'
                                    }}>
                                        Anchor #{currentAnchorForRemarks.anchorId || currentAnchorForRemarks.id}
                                    </span>
                                </h5>
                                <button 
                                    type="button" 
                                    className="close" 
                                    onClick={() => {
                                        setShowRemarksModal(false);
                                        setNewRemarkText('');
                                        setCurrentAnchorForRemarks(null);
                                    }}
                                    style={{
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        color: '#6c757d',
                                        opacity: '0.8'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.opacity = '1';
                                        e.target.style.color = '#dc3545';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.opacity = '0.8';
                                        e.target.style.color = '#6c757d';
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                {/* Existing Notes Section */}
                                <div className="mb-3">
                                    <h6 className="text-muted mb-2">
                                        <i className="fa fa-history"></i>
                                        Previous Notes ({remarksToDisplay ? remarksToDisplay.split('\n').length : 0})
                                    </h6>
                                    <div 
                                        style={{ 
                                            maxHeight: '200px', 
                                            overflowY: 'auto', 
                                            border: '1px solid #ddd', 
                                            borderRadius: '4px',
                                            padding: '10px',
                                            backgroundColor: '#f9f9f9'
                                        }}
                                    >
                                        {remarksToDisplay ? (
                                            remarksToDisplay.split('\n').map((note, idx) => (
                                                <div 
                                                    key={idx} 
                                                    style={{ 
                                                        marginBottom: '8px', 
                                                        paddingBottom: '8px', 
                                                        borderBottom: idx === remarksToDisplay.split('\n').length - 1 ? 'none' : '1px solid #eee',
                                                        whiteSpace: 'pre-wrap',
                                                        fontSize: '14px',
                                                        lineHeight: '1.4'
                                                    }}
                                                >
                                                    {note}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted mb-0">No previous notes available.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Add New Note Section */}
                                <div className="mt-4">
                                    <h6 className="text-primary mb-2">
                                        <i className="fa fa-plus-circle"></i>
                                        Add New Note
                                    </h6>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter your new note/remark here..."
                                        value={newRemarkText}
                                        onChange={(e) => setNewRemarkText(e.target.value)}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    ></textarea>
                                    <small className="text-muted">
                                        <i className="fa fa-info-circle"></i>
                                        Your note will be timestamped and added to the history above.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ borderTop: '2px solid #e9ecef' }}>
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={() => {
                                        setShowRemarksModal(false);
                                        setNewRemarkText('');
                                        setCurrentAnchorForRemarks(null);
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                    Close
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleRemarksModalSave}
                                    disabled={!newRemarkText.trim()}
                                    style={{
                                        opacity: newRemarkText.trim() ? 1 : 0.6
                                    }}
                                >
                                    <i className="fa fa-save"></i>
                                    Save Note
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewAnchorsPage;

